import React, { useState } from 'react';
import { PassiveSkill, PassiveSkillCategory, PassiveSkillFrequency, Stats } from '../types';
import { passiveSkillService } from '../services/passiveSkillService';
import { useLanguage } from '../contexts/LanguageContext';

interface CreatePassiveSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (skill: Omit<PassiveSkill, 'id' | 'userId'>) => void;
}

const CreatePassiveSkillModal: React.FC<CreatePassiveSkillModalProps> = ({
  isOpen,
  onClose,
  onCreate
}) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<PassiveSkillCategory>('physical');
  const [icon, setIcon] = useState('⚔️');
  const [frequency, setFrequency] = useState<PassiveSkillFrequency>('daily');
  const [targetDaysPerWeek, setTargetDaysPerWeek] = useState(3);
  const [allowedWeekdays, setAllowedWeekdays] = useState<number[]>([1,3,5]);
  const [targetDuration, setTargetDuration] = useState<number | undefined>(30);
  const [statToBoost, setStatToBoost] = useState<keyof Stats>('strength');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) return;

    // Validate target duration minimum
    if (targetDuration !== undefined && targetDuration < 7) {
      setErrorMessage('Goal duration must be at least 7 days for proper progression. Shorter goals can lead to instant level-ups!');
      return;
    }

    // Clear any previous error
    setErrorMessage('');

    const newSkill: Omit<PassiveSkill, 'id' | 'userId'> = {
      name: name.trim(),
      description: description.trim(),
      category,
      icon,
      frequency,
      targetDaysPerWeek: frequency === 'daily' ? undefined : targetDaysPerWeek,
      allowedWeekdays: frequency === 'custom' ? allowedWeekdays.slice().sort() : undefined,
      targetDuration: targetDuration || undefined,
      level: 1,
      currentStreak: 0,
      longestStreak: 0,
      totalCompletions: 0,
      statBoost: {
        stat: statToBoost,
        percentBoost: passiveSkillService.getStatBoostForLevel(1)
      },
      createdAt: Date.now(),
      lastCompletedDate: '',
      completionHistory: [],
      isActive: true
    };

    onCreate(newSkill);
    
    // Reset form
    setName('');
    setDescription('');
    setCategory('physical');
    setIcon('⚔️');
    setFrequency('daily');
    setTargetDaysPerWeek(3);
    setAllowedWeekdays([1,3,5]);
    setStatToBoost('strength');
  };

  const handleClose = () => {
    onClose();
    setName('');
    setDescription('');
    setCategory('physical');
    setIcon('⚔️');
    setFrequency('daily');
    setTargetDaysPerWeek(3);
    setAllowedWeekdays([1,3,5]);
    setTargetDuration(30);
    setStatToBoost('strength');
  };

  if (!isOpen) return null;

  const categories: Array<{ value: PassiveSkillCategory; label: string; icons: string[] }> = [
    { value: 'physical', label: t.passiveSkills.physical, icons: ['⚔️', '💪', '🏃', '🥊', '⚡', '🔥'] },
    { value: 'mental', label: t.passiveSkills.mental, icons: ['📚', '🧠', '📖', '✍️', '🎯', '💡'] },
    { value: 'social', label: t.passiveSkills.social, icons: ['🤝', '👥', '💬', '🌟', '❤️', '🎭'] },
    { value: 'spiritual', label: t.passiveSkills.spiritual, icons: ['🧘', '🕉️', '☮️', '🌿', '🌙', '✨'] },
    { value: 'productivity', label: t.passiveSkills.productivity, icons: ['⚡', '📊', '🎯', '⏰', '💼', '🚀'] }
  ];

  const selectedCategoryData = categories.find(c => c.value === category);

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-[100] p-2 sm:p-3 md:p-4 modal-backdrop animate-fade-in">
      <div className="relative max-w-sm sm:max-w-md md:max-w-xl lg:max-w-2xl w-full animate-scale-in">
        {/* Main Modal */}
        <div className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl shadow-2xl overflow-hidden"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-4 sm:p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-purple-500/20"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div>
              <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                <div className="w-2 h-2 bg-system-blue rounded-full animate-pulse"></div>
                <span className="text-xs sm:text-sm font-orbitron text-system-blue tracking-wider uppercase">
                  ［ System ］
                </span>
              </div>
              <h2 className="text-base sm:text-xl font-bold font-orbitron text-white tracking-wider">
                {t.passiveSkills.createSkill.toUpperCase()}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-white text-lg sm:text-xl leading-none transition-all bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-1.5 sm:p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
              style={{
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
              }}
            >
              ✕
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="p-3 sm:p-5 max-h-[calc(75vh-120px)] sm:max-h-[calc(85vh-140px)] overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-2.5 sm:space-y-3">
              {/* Skill Name */}
              <div>
                <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-2">
                  {t.passiveSkills.skillName} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-3 py-2.5 text-sm sm:text-base text-white font-orbitron placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                  placeholder={t.passiveSkills.skillNamePlaceholder}
                  required
                  maxLength={50}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase tracking-wide mb-2">
                  {t.passiveSkills.description}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-3 py-2.5 text-sm sm:text-base text-white font-rajdhani placeholder:text-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all resize-none"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                  placeholder={t.passiveSkills.descriptionPlaceholder}
                  rows={3}
                  maxLength={200}
                />
              </div>

              {/* Category & Icon Selection */}
              <div className="bg-gradient-to-b from-blue-950/40 via-slate-950/30 to-blue-950/40 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/40 border-r-2 border-b-2 border-r-blue-700/60 border-b-blue-700/60 rounded-2xl p-3 sm:p-5"
                style={{
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">🎯</span>
                  <label className="text-sm font-rajdhani text-gray-400 uppercase tracking-wide">
                    {t.passiveSkills.category}
                  </label>
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
                  {categories.map(cat => (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => {
                        setCategory(cat.value);
                        setIcon(cat.icons[0]);
                      }}
                      className={`p-2.5 sm:p-3 rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 font-rajdhani font-bold uppercase text-[10px] sm:text-xs transition-all relative ${
                        category === cat.value
                          ? 'border-t-blue-500/60 border-l-blue-500/60 border-r-blue-700/80 border-b-blue-700/80 bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm text-white scale-105'
                          : 'border-t-slate-500/40 border-l-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 backdrop-blur-sm text-gray-400 hover:text-white hover:border-blue-500/50 hover:scale-105'
                      }`}
                      style={category === cat.value ? {
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                      } : {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                      }}
                    >
                      {category === cat.value && (
                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                      )}
                      {cat.label}
                    </button>
                  ))}
                </div>

                {/* Icon Selection */}
                <div>
                  <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-2">
                    {t.passiveSkills.icon}
                  </label>
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <div className="text-3xl sm:text-4xl">{icon}</div>
                    <input
                      type="text"
                      value={icon}
                      onChange={(e) => setIcon(e.target.value)}
                      placeholder={t.passiveSkills.iconPlaceholder}
                      className="flex-1 bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl px-2.5 py-1.5 sm:px-3 sm:py-2 text-white font-orbitron focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all"
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                      }}
                      maxLength={2}
                    />
                  </div>
                  {selectedCategoryData && (
                    <div className="flex flex-wrap gap-2">
                      {selectedCategoryData.icons.map((suggestedIcon, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setIcon(suggestedIcon)}
                          className={`text-xl sm:text-2xl p-1.5 sm:p-2 rounded-xl border-t-2 border-l-2 border-r-2 border-b-2 transition-all hover:scale-110 relative ${
                            icon === suggestedIcon
                              ? 'border-t-blue-500/60 border-l-blue-500/60 border-r-blue-700/80 border-b-blue-700/80 bg-gradient-to-b from-blue-800/60 via-blue-900/50 to-blue-950/60 backdrop-blur-sm'
                              : 'border-t-slate-500/40 border-l-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 backdrop-blur-sm hover:border-blue-500/50'
                          }`}
                          style={{
                            boxShadow: icon === suggestedIcon 
                              ? '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                              : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                          }}
                        >
                          {icon === suggestedIcon && (
                            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                          )}
                          {suggestedIcon}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Frequency & Schedule */}
              <div className="bg-gradient-to-b from-purple-950/40 via-slate-950/30 to-purple-950/40 backdrop-blur-sm border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-2xl p-3 sm:p-5"
                style={{
                  boxShadow: '0 4px 12px rgba(168, 85, 247, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">📅</span>
                  <label className="text-sm font-rajdhani text-gray-400 uppercase tracking-wide">
                    {t.passiveSkills.frequency}
                  </label>
                </div>
                
                <select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as PassiveSkillFrequency)}
                  className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-3 py-2.5 text-white font-orbitron focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-all mb-3"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  <option value="daily">{t.passiveSkills.daily}</option>
                  <option value="weekly">{t.passiveSkills.weekly}</option>
                  <option value="custom">{t.passiveSkills.custom}</option>
                </select>

                {/* Target Duration */}
                <div>
                  <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-2">
                    Target Duration (Optional)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      value={targetDuration || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTargetDuration(value === '' ? undefined : Math.max(1, parseInt(value) || 1));
                        // Clear error message when user changes duration
                        setErrorMessage('');
                      }}
                      placeholder="e.g. 30"
                      min={1}
                      max={365}
                      className="flex-1 bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-xl px-3 py-2 text-white font-orbitron focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-all"
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                      }}
                    />
                    <span className="text-sm text-gray-400 font-rajdhani">days</span>
                  </div>
                  <p className="text-xs text-gray-600 font-rajdhani mt-1">
                    Leave empty for indefinite commitment
                  </p>
                </div>

                {/* Target Days Per Week */}
                {frequency === 'weekly' && (
                  <div>
                    <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-2">
                      {t.passiveSkills.targetDaysPerWeek}
                    </label>
                    <input
                      type="number"
                      value={targetDaysPerWeek}
                      onChange={(e) => setTargetDaysPerWeek(Math.max(1, Math.min(7, parseInt(e.target.value) || 1)))}
                      min={1}
                      max={7}
                      className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl px-3 py-2.5 text-white font-orbitron focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 transition-all"
                      style={{
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                      }}
                    />
                  </div>
                )}

                {/* Custom Weekdays Picker */}
                {frequency === 'custom' && (
                  <div>
                    <label className="block text-xs font-rajdhani text-gray-500 uppercase mb-2">
                      {t.passiveSkills.customDays}
                    </label>
                    <div className="grid grid-cols-7 gap-1.5 sm:gap-2 mb-2">
                      {[0,1,2,3,4,5,6].map((d) => (
                        <button
                          key={d}
                          type="button"
                          onClick={() => {
                            setAllowedWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
                          }}
                          className={`p-1.5 sm:p-2 rounded-xl border-t-2 border-l-2 border-r-2 border-b-2 text-xs sm:text-sm font-rajdhani uppercase transition-all relative ${
                            allowedWeekdays.includes(d) 
                              ? 'border-t-purple-500/60 border-l-purple-500/60 border-r-purple-700/80 border-b-purple-700/80 bg-gradient-to-b from-purple-800/60 via-purple-900/50 to-purple-950/60 backdrop-blur-sm text-white scale-105' 
                              : 'border-t-slate-500/40 border-l-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 backdrop-blur-sm text-gray-400 hover:text-white hover:border-purple-500/50 hover:scale-105'
                          }`}
                          style={allowedWeekdays.includes(d) ? {
                            boxShadow: '0 4px 12px rgba(168, 85, 247, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                          } : {
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                          }}
                        >
                          {allowedWeekdays.includes(d) && (
                            <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-xl"></div>
                          )}
                          {['S','M','T','W','T','F','S'][d]}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 font-rajdhani">{t.passiveSkills.customDaysHelp}</p>
                  </div>
                )}
              </div>

              {/* Stat to Boost */}
              <div className="bg-gradient-to-b from-green-950/40 via-slate-950/30 to-green-950/40 backdrop-blur-sm border-t-2 border-l-2 border-green-500/40 border-r-2 border-b-2 border-r-green-700/60 border-b-green-700/60 rounded-2xl p-3 sm:p-5"
                style={{
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">✨</span>
                  <label className="text-sm font-rajdhani text-gray-400 uppercase tracking-wide">
                    {t.passiveSkills.statBoost}
                  </label>
                </div>
                
                <p className="text-xs text-gray-500 font-rajdhani mb-3">
                  {t.passiveSkills.statBoostDesc}
                </p>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                  {(['strength', 'intelligence', 'agility', 'vitality', 'sense'] as Array<keyof Stats>).map(stat => (
                    <button
                      key={stat}
                      type="button"
                      onClick={() => setStatToBoost(stat)}
                      className={`p-2.5 sm:p-3 rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 font-rajdhani font-bold uppercase text-[10px] sm:text-xs transition-all relative ${
                        statToBoost === stat
                          ? 'border-t-green-500/60 border-l-green-500/60 border-r-green-700/80 border-b-green-700/80 bg-gradient-to-b from-green-800/60 via-green-900/50 to-green-950/60 backdrop-blur-sm text-white scale-105'
                          : 'border-t-slate-500/40 border-l-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 bg-gradient-to-b from-slate-800/30 via-slate-900/30 to-slate-950/30 backdrop-blur-sm text-gray-400 hover:text-white hover:border-green-500/50 hover:scale-105'
                      }`}
                      style={statToBoost === stat ? {
                        boxShadow: '0 4px 12px rgba(34, 197, 94, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                      } : {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                      }}
                    >
                      {statToBoost === stat && (
                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                      )}
                      {t.stats[stat as keyof typeof t.stats]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-gradient-to-b from-amber-950/40 via-yellow-950/30 to-amber-950/40 backdrop-blur-sm border-t-2 border-l-2 border-amber-500/40 border-r-2 border-b-2 border-r-amber-700/60 border-b-amber-700/60 rounded-2xl p-3 sm:p-5"
                style={{
                  boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div className="flex items-start gap-3">
                  <span className="text-xl sm:text-2xl">💡</span>
                  <div className="flex-1">
                    <h4 className="font-orbitron font-bold text-amber-400 text-sm sm:text-base mb-1">{t.passiveSkills.howItWorks}</h4>
                    <ul className="text-xs sm:text-sm text-gray-400 font-rajdhani space-y-1">
                      <li>{t.passiveSkills.howItWorksDesc1}</li>
                      <li>{t.passiveSkills.howItWorksDesc2}</li>
                      <li>{t.passiveSkills.howItWorksDesc3}</li>
                      <li>{t.passiveSkills.howItWorksDesc4}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="bg-red-950/50 border border-red-500/50 rounded-xl p-3">
                  <div className="flex items-center gap-2">
                    <span className="text-red-400 text-lg">⚠️</span>
                    <span className="text-red-300 font-rajdhani text-sm">{errorMessage}</span>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 pt-1.5 sm:pt-2">
                <button
                  type="submit"
                  disabled={!name.trim()}
                  className="flex-1 relative group bg-gradient-to-b from-blue-800/60 via-purple-800/50 to-blue-950/60 backdrop-blur-sm border-t-2 border-l-2 border-blue-500/50 border-r-2 border-b-2 border-r-purple-700/70 border-b-purple-700/70 rounded-2xl px-3 py-2.5 sm:px-4 sm:py-3 md:px-5 md:py-3.5 font-orbitron font-bold text-xs sm:text-sm tracking-wider transition-all hover:scale-105 hover:shadow-2xl hover:shadow-purple-500/50 overflow-hidden text-white disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)'
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/20 rounded-2xl"></div>
                  <span className="relative z-10">{t.passiveSkills.createButton.toUpperCase()}</span>
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-3 py-2.5 sm:px-4 sm:py-3 md:px-5 md:py-3.5 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl font-orbitron font-bold text-xs sm:text-sm tracking-wider transition-all hover:scale-105"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {t.common.cancel.toUpperCase()}
                  <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-2xl"></div>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePassiveSkillModal;
